import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment.development";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { SubmissionUtils } from "../../features/classic/submission-table-lab/submission-utils";

export type SubmissionListByFilter = 'PROCESSED' | 'PROCEED' | 'FLAGGED' | 'DECLINE' ;
export type SubmissionListByFilterPayload = {
    filter: SubmissionListByFilter;
    page: number;
    limit: number;
};
export type SubmissionStatusResponse = {
    status?: string;
    submission_id?: string;
    is_processed?: boolean;
    document?: boolean;
    risk_appetite_score?: {
        submission_id?: string;
        lob_type?: string;
        final_risk_appetite_score?: string | number | null;
    };
    submission_acceptance_check?: {
        "evaluation_status"?: string,
        "evaluation_result"?: any
    }
};
export type SubmissionListByFilterResponse = {
    status?: string;
    data?: Array<{
        submission_id?: string | number;
        decision?: string;
        risk_appetite_score?: number | null;
    }>;
    total_count?: number;
    page?: number;
    limit?: number;
    has_next_page?: boolean;
    has_prev_page?: boolean;
};

export type UnderwriterSubmissionPayload = {
    user_id: number;
    page: number;
    limit: number;
    filter_flag: number;
    underwriter: string;
    include_deleted: number;
    submission_id: string;
    quote_desc: string;
    policy_number: string;
    status: string;
    submit_date: string;
    effective_date: string;
    premium: string;
    insured_id: string;
    dot_number: string;
    insured_name: string;
    short_name: number[];
    agency: string;
    claim_count: string;
    treaty_description: string;
    exp_date: string;
    cancellation_date: string;
    lob: string;
    risk_company: string;
    source_portal: string;
    agency_id: string;
    agency_state: string;
    status_id_int: number[];
    treaty_id_int: string;
    underwriter_id?: number | '';
    quote_id?: string;
    risk_appetite?: string;
    bind_propensity?: string;
    data_quality_score?: string;
};
export type ProcessSubmissionPayload = {
    user_id: number;
    quote_id: string;
    submission_id: string;
    application_type_id: string;
};
export type UnderwriterSubmissionRecord = Record<string, unknown>;
export type UnderwriterSubmissionResponse = {
    status?: number | string;
    message?: string;
    statusCode?: number;
    data?: {
        records?: UnderwriterSubmissionRecord[];
        total_count?: number;
        page?: number;
        limit?: number;
        has_next_page?: boolean;
        has_prev_page?: boolean;
        next_page?: number | null;
    };
    rows?: UnderwriterSubmissionRecord[];
    submissions?: UnderwriterSubmissionRecord[];
    result?: UnderwriterSubmissionRecord[];
    records?: UnderwriterSubmissionRecord[];
};
export type ApiParamValue = string | number | boolean;
export type ApiQueryParams = Record<string, ApiParamValue | readonly ApiParamValue[] | null | undefined>;
export type ApiHttpOptions = {
    headers?: HttpHeaders | Record<string, string | string[]>;
    params?: ApiQueryParams;
    withCredentials?: boolean;
};
@Injectable({ providedIn: 'root' })
export class DataAggregationService {
    baseUrl = environment.apiBaseUrl;
    futBaseUrl = environment.futBaseUrl;
    assistantBaseUrl = (environment as { assistantApiBaseUrl?: string }).assistantApiBaseUrl ?? '';
    constructor(private readonly http: HttpClient) { }

    get<T>(endpoint: string, params?: ApiQueryParams, options?: Omit<ApiHttpOptions, 'params'>): Observable<T> {
        return this.http.get<T>(this.resolveUrl(endpoint), this.buildOptions(params, options));
    }

    post<T>(endpoint: string, body: any, options?: ApiHttpOptions): Observable<T> {
        return this.http.post<T>(this.resolveUrl(endpoint), body, this.buildOptions(options?.params, options));
    }

    put<T>(endpoint: string, body: any, options?: ApiHttpOptions): Observable<T> {
        return this.http.put<T>(this.resolveUrl(endpoint), body, this.buildOptions(options?.params, options));
    }

    patch<T>(endpoint: string, body: any, options?: ApiHttpOptions): Observable<T> {
        return this.http.patch<T>(this.resolveUrl(endpoint), body, this.buildOptions(options?.params, options));
    }

    delete<T>(endpoint: string, options?: ApiHttpOptions): Observable<T> {
        return this.http.delete<T>(this.resolveUrl(endpoint), this.buildOptions(options?.params, options));
    }

    private buildOptions(params?: ApiQueryParams, options?: Omit<ApiHttpOptions, 'params'>) {
        const httpParams = new HttpParams({
            fromObject: (params ?? {}) as Record<string, string | string[]>
        });

        return {
            headers: options?.headers,
            params: httpParams,
            withCredentials: options?.withCredentials
        };
    }

    private mergeHeaders(
        baseHeaders: HttpHeaders,
        extraHeaders?: HttpHeaders | Record<string, string | string[]>
    ): HttpHeaders {
        if (!extraHeaders) {
            return baseHeaders;
        }

        const normalizedExtraHeaders = extraHeaders instanceof HttpHeaders
            ? extraHeaders
            : new HttpHeaders(extraHeaders);

        return normalizedExtraHeaders.keys().reduce((headers, key) => {
            const values = normalizedExtraHeaders.getAll(key);

            if (!values || values.length === 0) {
                return headers;
            }

            return headers.set(key, values.length === 1 ? values[0] : values);
        }, baseHeaders);
    }

    private resolveUrl(endpoint: string): string {
        if (/^https?:\/\//i.test(endpoint)) {
            return endpoint;
        }

        return `${this.baseUrl}${endpoint}`;
    }

    processSubmission(payload: ProcessSubmissionPayload, options?: ApiHttpOptions) {
        return this.post<unknown>('process-submission', {
            ...payload,
            user_id: String(payload.user_id)
        }, options);
    }
    loadSubmissions(payload: UnderwriterSubmissionPayload, options?: ApiHttpOptions) {
        return this.post<UnderwriterSubmissionResponse>(`${this.futBaseUrl}getsubmissionsforunderwriterPaginated`, payload, options).pipe(
            map((response: any) => {
                response.data.records = response?.data?.records?.map((record: any) => ({
                    ...record,
                    classified_status: SubmissionUtils.getClassifiedStatus(record?.status ?? '')
                }));

                return response;
            }));
    }

    getSubmissions(params?: ApiQueryParams, options?: Omit<ApiHttpOptions, 'params'>) {
        return this.get<any>('get-all-submissions', params, options);
    }
    listSubmissionsByFilter(payload: SubmissionListByFilterPayload, options?: ApiHttpOptions) {
        return this.post<SubmissionListByFilterResponse>('submissions/list-by-filter', payload, options);
    }
    getSubmissionStatus(
        submissionId: string | number,
        quoteId: string | number,
        options?: ApiHttpOptions
    ): Observable<SubmissionStatusResponse> {
        return this.get<SubmissionStatusResponse>(`check_submission_status/${submissionId}/${quoteId}`, undefined, options);
    }
    getRiskProfileDetails(submissionId: string | number, applicationTypeId: string | number): Observable<any> {
        return this.get<any>(`risk-profile/v2/${submissionId}/${applicationTypeId}`);
    }
    getRiskProfileDetails492(submissionId: string | number): Observable<any> {
        return this.get<any>(`risk-profile/${submissionId}`);
    }
} 
